import { useState, useEffect, useCallback, useRef } from 'react';
import { AUDIO_CONFIG } from './constants';


// Custom hook for audio state management
export const useAudioState = (meetingDuration) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(meetingDuration ?? 0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [streamedDuration, setStreamedDuration] = useState(0);
  const [hasStreamInitiated, setHasStreamInitiated] = useState(false);
  const [streamStartOffset, setStreamStartOffset] = useState(0);
  const [pendingPlay, setPendingPlay] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);

  // Update duration whenever meetingDuration prop changes
  useEffect(() => {
    setDuration(meetingDuration ?? 0);
  }, [meetingDuration]);

  const resetState = useCallback(() => {
    setHasStreamInitiated(false);
    setError(null);
    setCurrentTime(0);
    setStreamedDuration(0);
    setStreamStartOffset(0);
    setIsPlaying(false);
    setIsLoading(false);
    setPendingPlay(false);
  }, []);

  return {
    // State values
    isPlaying, isLoading, currentTime, duration, volume, isMuted,
    loadProgress, error, streamedDuration, hasStreamInitiated, 
    streamStartOffset, pendingPlay, playbackRate,
    // State setters
    setIsPlaying, setIsLoading, setCurrentTime, setVolume, setIsMuted,
    setLoadProgress, setError, setStreamedDuration, setHasStreamInitiated, 
    setStreamStartOffset, setPendingPlay, setPlaybackRate,
    // Utilities
    resetState, setDuration
  };
};

// Custom hook for container width detection
export const useContainerWidth = (playerRef) => {
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      if (playerRef.current) {
        setContainerWidth(playerRef.current.clientWidth);
      }
    };

    const resizeObserver = new ResizeObserver(updateWidth);
    if (playerRef.current) {
      resizeObserver.observe(playerRef.current);
      updateWidth(); // Initial measurement
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [playerRef]);

  return containerWidth;
};

// Custom hook for optimized timeline drawing with layered rendering
export const useTimelineDrawing = (canvasRef, segments, currentTime, duration, streamedDuration) => {
  // Cache for static background to avoid redrawing segments
  const backgroundCache = useRef(null);
  const lastSegmentsHash = useRef(null);
  const lastStreamedDuration = useRef(null);
  const lastCanvasSize = useRef({ width: 0, height: 0 });

  // Generate a hash for segments to detect changes
  const getSegmentsHash = useCallback((segments, duration, streamedDuration) => {
    return `${segments.map(s => `${s.start}-${s.end}-${s.color}`).join('|')}-${duration}-${streamedDuration}`;
  }, []);

  // Draw static background (segments + streamed portion) - only when needed
  const drawBackground = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !duration) return null;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    // Create background canvas if needed or if size changed
    const currentSize = { width: canvas.width, height: canvas.height };
    if (!backgroundCache.current || 
        lastCanvasSize.current.width !== currentSize.width || 
        lastCanvasSize.current.height !== currentSize.height) {
      
      backgroundCache.current = document.createElement('canvas');
      backgroundCache.current.width = canvas.width;
      backgroundCache.current.height = canvas.height;
      lastCanvasSize.current = currentSize;
    }

    const bgCtx = backgroundCache.current.getContext('2d');
    if (!bgCtx) return null;

    // Clear background canvas
    bgCtx.clearRect(0, 0, backgroundCache.current.width, backgroundCache.current.height);

    // Set device pixel ratio scaling
    const dpr = window.devicePixelRatio || 1;
    bgCtx.setTransform(dpr, 0, 0, dpr, 0, 0);

    if (segments.length > 0) {
      // Draw speaker segments
      segments.forEach(segment => {
        const startX = (segment.start / duration) * width;
        const endX = (segment.end / duration) * width;
        const segmentWidth = Math.max(endX - startX, 1);

        bgCtx.fillStyle = segment.color;
        bgCtx.fillRect(startX, 0, segmentWidth, height);

        // Subtle borders for wider segments
        if (segmentWidth > 2) {
          bgCtx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
          bgCtx.lineWidth = 0.5;
          bgCtx.strokeRect(startX, 0, segmentWidth, height);
        }
      });
    }

    // Draw streamed portion
    if (streamedDuration > 0) {
      const streamedX = (streamedDuration / duration) * width;
      bgCtx.fillStyle = 'rgba(76, 175, 80, 0.2)';
      bgCtx.fillRect(0, 0, streamedX, height);
    }

    return backgroundCache.current;
  }, [canvasRef, segments, duration, streamedDuration]);

  // Draw only the progress indicator (fast operation)
  const drawProgressIndicator = useCallback((ctx, width, height, clearPrevious = true) => {
    if (!duration) return;

    const progressX = (currentTime / duration) * width;
    
    if (clearPrevious) {
      // Only clear the area where progress indicator was/will be
      const clearWidth = 6; // 3px on each side of the indicator
      const prevProgressX = Math.max(0, progressX - clearWidth/2);
      const clearArea = Math.min(width, clearWidth);
      
      // Restore background in the progress area
      if (backgroundCache.current) {
        ctx.drawImage(
          backgroundCache.current,
          prevProgressX, 0, clearArea, height,
          prevProgressX, 0, clearArea, height
        );
      }
    }

    // Draw new progress indicator
    ctx.fillStyle = '#1976d2';
    ctx.fillRect(progressX - 1, 0, 2, height);

    // Soft shadow effect
    ctx.shadowColor = '#1976d2';
    ctx.shadowBlur = 2;
    ctx.fillRect(progressX - 0.5, 0, 1, height);
    ctx.shadowBlur = 0;
  }, [currentTime, duration]);

  // Main draw function - optimized to minimize redraws
  const drawTimeline = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !duration) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    // Check if background needs redrawing
    const currentSegmentsHash = getSegmentsHash(segments, duration, streamedDuration);
    const needsBackgroundRedraw = (
      currentSegmentsHash !== lastSegmentsHash.current ||
      streamedDuration !== lastStreamedDuration.current ||
      !backgroundCache.current
    );

    if (needsBackgroundRedraw) {
      // Redraw background and full canvas
      const bgCanvas = drawBackground();
      if (bgCanvas) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(bgCanvas, 0, 0);
        drawProgressIndicator(ctx, width, height, false); // Don't clear since we just drew background
      }
      
      lastSegmentsHash.current = currentSegmentsHash;
      lastStreamedDuration.current = streamedDuration;
    } else {
      // Only update progress indicator (much faster)
      drawProgressIndicator(ctx, width, height, true);
    }
  }, [segments, currentTime, duration, streamedDuration, getSegmentsHash, drawBackground, drawProgressIndicator]);

  // Force full redraw function (for external use when needed)
  const forceRedraw = useCallback(() => {
    lastSegmentsHash.current = null; // Force background redraw
    drawTimeline();
  }, [drawTimeline]);

  return { drawTimeline, forceRedraw };
};

// Custom hook for audio streaming
export const useAudioStreaming = (meetingId, audioRef, streamControllerRef, audioState, ServerURL, formatTimepoint, getCachedAudio, setCachedAudio) => {
  const {
    setIsLoading, setError, setStreamedDuration, setStreamStartOffset, setPendingPlay,
    volume, isMuted, streamStartOffset, duration
  } = audioState;

  // Enhanced buffered range checking with better tolerance
  const isWithinBufferedRange = useCallback((seekTime) => {
    const audio = audioRef.current;
    if (!audio || !audio.buffered || audio.buffered.length === 0) return false;

    const rel = seekTime - streamStartOffset;
    // Use larger tolerance for better user experience
    const tolerance = AUDIO_CONFIG.TOLERANCE_EPS * 2; // 500ms tolerance
    
    for (let i = 0; i < audio.buffered.length; i++) {
      const start = audio.buffered.start(i);
      const end = audio.buffered.end(i);
      if (rel >= start - tolerance && rel <= end + tolerance) {
        return { inRange: true, bufferIndex: i, relativeTime: rel };
      }
    }
    return { inRange: false };
  }, [streamStartOffset]);

  // Check if we can seek within a reasonable distance from current stream
  const canSeekWithinCurrentStream = useCallback((seekTime) => {
    const audio = audioRef.current;
    if (!audio || !streamStartOffset) return false;

    // If seeking within configured window of stream start, try to use current stream
    const streamDistance = Math.abs(seekTime - streamStartOffset);
    return streamDistance <= AUDIO_CONFIG.SEEK_WINDOW_SECONDS;
  }, [streamStartOffset]);



  const loadStreamingAudio = useCallback(async (startTime = null, shouldAutoPlay = false) => {
    if (!meetingId || audioState.isLoading) return;

    const streamStartTime = startTime !== null ? startTime : Math.floor(audioState.currentTime || 0);

    // Enhanced smart seeking logic
    const bufferCheck = isWithinBufferedRange(streamStartTime);
    
    // Priority 1: Seek within already buffered content
    if (streamStartTime !== 0 && bufferCheck.inRange) {
      console.log('🎯 Smart seek within buffered range:', formatTimepoint(streamStartTime));
      const audio = audioRef.current;
      if (audio) {
        audio.currentTime = Math.max(0, bufferCheck.relativeTime);
        if (shouldAutoPlay) {
          audio.play().catch(e => console.warn('Autoplay prevented:', e));
        }
      }
      return;
    }

    // Priority 2: Try to seek within current stream if close enough
    if (streamStartTime !== 0 && canSeekWithinCurrentStream(streamStartTime)) {
      console.log('🎯 Attempting seek within current stream:', formatTimepoint(streamStartTime));
      const audio = audioRef.current;
      if (audio && audio.src) {
        const relativeTime = streamStartTime - streamStartOffset;
        // Only seek if the relative time makes sense
        if (relativeTime >= 0) {
          try {
            audio.currentTime = relativeTime;
            if (shouldAutoPlay) {
              audio.play().catch(e => console.warn('Autoplay prevented:', e));
            }
            // Update our current time tracking
            audioState.setCurrentTime(streamStartTime);
            return;
          } catch (e) {
            console.log('⚠️ Failed to seek within current stream, will load new stream');
            // Fall through to load new stream
          }
        }
      }
    }

    const cacheKey = `${meetingId}_${streamStartTime}`;

    console.log('🎵 Loading streaming audio from:', formatTimepoint(streamStartTime));

    setIsLoading(true);
    setError(null);
    setStreamedDuration(streamStartTime);
    setStreamStartOffset(streamStartTime);

    if (shouldAutoPlay) {
      setPendingPlay(true);
    }

    try {
      // Check cache first
      const cachedAudio = getCachedAudio(cacheKey);
      if (cachedAudio) {
        console.log('✅ Using cached audio for position:', formatTimepoint(streamStartTime));
        const audio = audioRef.current;
        if (audio) {
          // Clear old source to cancel ongoing request
          audio.pause();
          audio.removeAttribute('src');
          audio.load();

          audio.src = cachedAudio.url;
          audio.volume = isMuted ? 0 : volume;
          audio.muted = isMuted;
          audio.playbackRate = audioState.playbackRate;
          audio.load();
        }
        return;
      }

      // Generate the URL
      const streamUrl = `${ServerURL}/stream_audio/${meetingId}/${streamStartTime}/-1`;
      console.log('📡 Streaming from:', streamUrl);

      // Cache the URL for future use
      setCachedAudio(cacheKey, {
        url: streamUrl,
        startTime: streamStartTime
      });

      // Set audio source
      const audio = audioRef.current;
      if (audio) {
        // Clear old source to cancel ongoing request
        audio.pause();
        audio.removeAttribute('src');
        audio.load();

        audio.src = streamUrl;
        audio.volume = isMuted ? 0 : volume;
        audio.muted = isMuted;
        audio.playbackRate = audioState.playbackRate;
        audio.load();
      }

      console.log('✅ Audio stream initiated from', formatTimepoint(streamStartTime));
    } catch (error) {
      console.error('❌ Streaming error:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Streaming failed';
      if (error.message.includes('network')) {
        errorMessage = 'Network error - check your connection';
      } else if (error.message.includes('not supported')) {
        errorMessage = 'Audio format not supported by browser';
      } else if (error.message.includes('aborted')) {
        errorMessage = 'Audio loading was interrupted';
      } else {
        errorMessage = `Streaming failed: ${error.message || 'Unknown error'}`;
      }
      
      setError(errorMessage);
      setIsLoading(false);
      setPendingPlay(false);
    }
  }, [
    meetingId,
    audioState.isLoading,
    audioState.currentTime,
    volume,
    isMuted,
    audioRef,
    setIsLoading,
    setError,
    setStreamedDuration,
    setStreamStartOffset,
    setPendingPlay,
    isWithinBufferedRange,
    streamStartOffset
  ]);

  return { 
    loadStreamingAudio
  };
};
